import React, { useEffect, useReducer, useState, useContext } from "react";

import {
  Button,
  Chip,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Grid,
} from "@material-ui/core";

import MainHeader from "../../components/MainHeader";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { DeleteOutline, Edit, Add, AccountTree } from "@material-ui/icons";
import QueueModal from "../../components/QueueModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";

const useStyles = makeStyles((theme) => ({
  pageRoot: {
    flex: 1,
    width: "100%",
    padding: theme.spacing(2.5),
    height: "calc(100% - 48px)",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
      gap: theme.spacing(1.5),
    },
  },
  controlsPaper: {
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
    padding: theme.spacing(1.25, 1.5),
    backgroundColor: theme.palette.background.paper,
  },
  addButton: {
    minHeight: 38,
    borderRadius: 8,
    fontWeight: 600,
    fontSize: "0.8rem",
    padding: theme.spacing(0.75, 2),
    textTransform: "none",
    boxShadow: "0 2px 8px rgba(59,130,246,0.22)",
    "&:hover": {
      boxShadow: "0 4px 14px rgba(59,130,246,0.32)",
    },
  },
  mainPaper: {
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles,
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
  },
  tableHeaderCell: {
    fontWeight: 700,
    fontSize: "0.68rem",
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.03)"
        : "rgba(15,23,42,0.025)",
    borderBottom: `2px solid ${theme.palette.divider}`,
    padding: theme.spacing(1.25, 2),
    whiteSpace: "nowrap",
  },
  tableRow: {
    transition: "background-color 0.15s",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.025)"
          : "rgba(59,130,246,0.03)",
    },
    "&:last-child td, &:last-child th": {
      borderBottom: 0,
    },
  },
  tableCell: {
    fontSize: "0.8rem",
    padding: theme.spacing(1.25, 2),
  },
  idCell: {
    fontFamily: "'JetBrains Mono', 'Fira Mono', monospace",
    fontSize: "0.72rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    letterSpacing: "0.02em",
  },
  nameCell: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  colorSwatch: {
    width: 52,
    height: 18,
    borderRadius: 5,
    border: `1px solid ${theme.palette.divider}`,
    display: "inline-block",
  },
  greetingText: {
    fontSize: "0.77rem",
    color: theme.palette.text.secondary,
    maxWidth: 280,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "block",
  },
  actionCell: {
    padding: theme.spacing(0.75, 1.5),
    whiteSpace: "nowrap",
  },
  editBtn: {
    width: 30,
    height: 30,
    borderRadius: 7,
    border: `1px solid ${theme.palette.divider}`,
    marginRight: 4,
    color: theme.palette.text.secondary,
    transition: "all 0.15s",
    "&:hover": {
      color: theme.palette.type === "dark" ? "#60a5fa" : "#3b82f6",
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(96,165,250,0.1)"
          : "rgba(59,130,246,0.07)",
      borderColor:
        theme.palette.type === "dark"
          ? "rgba(96,165,250,0.3)"
          : "rgba(59,130,246,0.25)",
    },
  },
  deleteBtn: {
    width: 30,
    height: 30,
    borderRadius: 7,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    transition: "all 0.15s",
    "&:hover": {
      color: theme.palette.type === "dark" ? "#f87171" : "#ef4444",
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(248,113,113,0.1)"
          : "rgba(239,68,68,0.07)",
      borderColor:
        theme.palette.type === "dark"
          ? "rgba(248,113,113,0.3)"
          : "rgba(239,68,68,0.25)",
    },
  },
  emptyRow: {
    "&:hover": { backgroundColor: "transparent !important" },
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(5, 2),
    color: theme.palette.text.secondary,
    gap: theme.spacing(1),
  },
  emptyStateIcon: {
    fontSize: 40,
    opacity: 0.25,
  },
  emptyStateText: {
    fontSize: "0.82rem",
    opacity: 0.5,
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_QUEUES") {
    const queues = action.payload;
    const newQueues = [];

    queues.forEach((queue) => {
      const queueIndex = state.findIndex((q) => q.id === queue.id);
      if (queueIndex !== -1) {
        state[queueIndex] = queue;
      } else {
        newQueues.push(queue);
      }
    });

    return [...state, ...newQueues];
  }

  if (action.type === "UPDATE_QUEUES") {
    const queue = action.payload;
    const queueIndex = state.findIndex((u) => u.id === queue.id);

    if (queueIndex !== -1) {
      state[queueIndex] = queue;
      return [...state];
    }
    return [queue, ...state];
  }

  if (action.type === "DELETE_QUEUE") {
    const queueId = action.payload;
    const queueIndex = state.findIndex((q) => q.id === queueId);
    if (queueIndex !== -1) {
      state.splice(queueIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const Queues = () => {
  const classes = useStyles();

  const [queues, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);

  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const { user, socket } = useContext(AuthContext);
  const companyId = user.companyId;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/queue");
        dispatch({ type: "LOAD_QUEUES", payload: data });
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const onQueueEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUEUES", payload: data.queue });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUEUE", payload: data.queueId });
      }
    };
    socket.on(`company-${companyId}-queue`, onQueueEvent);

    return () => {
      socket.off(`company-${companyId}-queue`, onQueueEvent);
    };
  }, [socket, companyId]);

  const handleOpenQueueModal = () => {
    setQueueModalOpen(true);
    setSelectedQueue(null);
  };

  const handleCloseQueueModal = () => {
    setQueueModalOpen(false);
    setSelectedQueue(null);
  };

  const handleEditQueue = (queue) => {
    setSelectedQueue(queue);
    setQueueModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedQueue(null);
  };

  const handleDeleteQueue = async (queueId) => {
    try {
      await api.delete(`/queue/${queueId}`);
      toast.success(i18n.t("Queue deleted successfully!"));
    } catch (err) {
      toastError(err);
    }
    setSelectedQueue(null);
  };

  return (
    <div className={classes.pageRoot}>
      <ConfirmationModal
        title={
          selectedQueue &&
          `${i18n.t("queues.confirmationModal.deleteTitle")} ${selectedQueue.name}?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeleteQueue(selectedQueue.id)}
      >
        {i18n.t("queues.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <QueueModal
        open={queueModalOpen}
        onClose={handleCloseQueueModal}
        queueId={selectedQueue?.id}
        onEdit={(res) => {
          if (res) {
            setTimeout(() => {
              handleEditQueue(res);
            }, 500);
          }
        }}
      />
      {user.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <>
          <MainHeader>
            <Grid container style={{ width: "100%" }}>
              <Grid item xs={12}>
                <Paper elevation={0} className={classes.controlsPaper}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4} md={3}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={handleOpenQueueModal}
                        className={classes.addButton}
                        startIcon={<Add />}
                      >
                        {i18n.t("queues.buttons.add")}
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </MainHeader>

          <Paper className={classes.mainPaper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center" className={classes.tableHeaderCell}>
                    {i18n.t("queues.table.ID")}
                  </TableCell>
                  <TableCell className={classes.tableHeaderCell}>
                    {i18n.t("queues.table.name")}
                  </TableCell>
                  <TableCell align="center" className={classes.tableHeaderCell}>
                    {i18n.t("queues.table.color")}
                  </TableCell>
                  <TableCell align="center" className={classes.tableHeaderCell}>
                    {i18n.t("queues.table.orderQueue")}
                  </TableCell>
                  <TableCell className={classes.tableHeaderCell}>
                    {i18n.t("queues.table.greeting")}
                  </TableCell>
                  <TableCell align="right" className={classes.tableHeaderCell}>
                    {i18n.t("queues.table.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {queues.map((queue) => (
                  <TableRow key={queue.id} className={classes.tableRow}>
                    <TableCell
                      align="center"
                      className={`${classes.tableCell} ${classes.idCell}`}
                    >
                      #{queue.id}
                    </TableCell>
                    <TableCell
                      className={`${classes.tableCell} ${classes.nameCell}`}
                    >
                      {queue.name}
                    </TableCell>
                    <TableCell align="center" className={classes.tableCell}>
                      <span
                        className={classes.colorSwatch}
                        style={{ backgroundColor: queue.color }}
                      />
                    </TableCell>
                    <TableCell align="center" className={classes.tableCell}>
                      {queue.orderQueue}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <span className={classes.greetingText}>
                        {queue.greetingMessage}
                      </span>
                    </TableCell>
                    <TableCell align="right" className={classes.actionCell}>
                      <IconButton
                        size="small"
                        className={classes.editBtn}
                        onClick={() => handleEditQueue(queue)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        className={classes.deleteBtn}
                        onClick={() => {
                          setSelectedQueue(queue);
                          setConfirmModalOpen(true);
                        }}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {loading && <TableRowSkeleton columns={6} />}
                {!loading && queues.length === 0 && (
                  <TableRow className={classes.emptyRow}>
                    <TableCell colSpan={6} style={{ border: 0 }}>
                      <div className={classes.emptyState}>
                        <AccountTree className={classes.emptyStateIcon} />
                        <Typography className={classes.emptyStateText}>
                          Nenhuma fila cadastrada
                        </Typography>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </div>
  );
};

export default Queues;
